import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { IDS } from "@/constants/testIds";
import { PROJECTS, PROJECT_IMAGES } from "@/data/content";

function ProjectCard({ p, index }) {
  const [hover, setHover] = useState(false);
  const img = PROJECT_IMAGES[p.slug];

  return (
    <motion.article
      data-testid={IDS.projects.card(p.slug)}
      data-cursor="hover"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: index * 0.05, duration: 0.5 }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="group relative border border-border bg-[#0A0A0A] overflow-hidden brut-hover"
      style={{ "--tw-shadow-color": p.accent }}
    >
      <div className="grid grid-cols-1 md:grid-cols-12 min-h-[280px]">
        {/* Image side */}
        <div className="md:col-span-5 lg:col-span-4 relative overflow-hidden border-b md:border-b-0 md:border-r border-border">
          <div
            className="absolute inset-0 transition-transform duration-500 group-hover:scale-105"
            style={{
              backgroundImage: `url(${img})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: hover ? "grayscale(0%) contrast(1.05)" : "grayscale(70%) contrast(1.1)",
            }}
          />
          <div className="absolute inset-0 mix-blend-multiply opacity-40" style={{ backgroundColor: p.accent }} />
          <div className="relative h-full min-h-[180px] flex items-end p-4">
            <span
              className="font-jet text-[10px] uppercase tracking-widest px-2 py-1 border border-[#F4F4F0]/40 backdrop-blur-sm bg-[#0A0A0A]/60"
            >
              {p.kind}
            </span>
          </div>
        </div>

        {/* Text side */}
        <div className="md:col-span-7 lg:col-span-8 p-6 sm:p-8 flex flex-col justify-between">
          <div>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <h3 className="font-mono-brut font-black uppercase text-3xl sm:text-4xl lg:text-5xl leading-[0.9] tracking-tighter">
                {p.name}
              </h3>
            </div>
            <div className="mt-2 font-jet text-[11px] uppercase tracking-widest text-muted-foreground">
              {p.client} · {p.years}
            </div>
            <p className="mt-5 text-sm sm:text-base text-foreground/80 leading-relaxed max-w-2xl">
              {p.summary}
            </p>
          </div>

          <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex flex-wrap gap-2">
              {p.stack.map((s) => (
                <span
                  key={s}
                  className="font-jet text-[10px] uppercase tracking-widest border border-border px-2 py-1"
                >
                  {s}
                </span>
              ))}
            </div>
            {p.liveUrl ? (
              <a
                data-testid={IDS.projects.link(p.slug)}
                href={p.liveUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-mono-brut text-xs uppercase tracking-widest hover:text-[#E3FF00]"
              >
                View live <ArrowUpRight size={14} />
              </a>
            ) : (
              <span
                data-testid={IDS.projects.link(p.slug)}
                className="inline-flex items-center gap-1 font-jet text-[10px] uppercase tracking-widest text-muted-foreground"
              >
                Preview soon
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function Projects() {
  return (
    <section
      id="projects"
      data-testid={IDS.projects.root}
      className="border-b border-border py-24"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="flex items-center gap-3 font-jet text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-4">
          <span>02</span><span className="h-px w-8 bg-border"></span><span>Selected work</span>
        </div>

        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <h2 className="font-mono-brut font-black uppercase text-4xl sm:text-6xl tracking-tighter leading-none">
            The <span className="text-[#E3FF00]">receipts.</span>
          </h2>
          <p className="max-w-md text-sm text-foreground/60">
            Five recent products I&apos;ve architected, engineered, or scaled — from
            SaaS platforms to CRM integrations. Live previews being added as clients approve.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {PROJECTS.map((p, i) => (
            <ProjectCard key={p.slug} p={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
